/**
 * Routes, and the one gate in front of them.
 *
 * `/me` answers 200 with `user: null` when signed out, so the gate is a single
 * query rather than a 401-catching interceptor: while it is in flight we render
 * nothing but the shell background, because flashing the login form at someone who
 * is already signed in is worse than a blank half-second.
 */

import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useMe } from "@/lib/queries";
import { AccountsPage } from "@/pages/accounts-page";
import { AgentPage } from "@/pages/agent-page";
import { CrmFeedPage } from "@/pages/crm-feed-page";
import { CrmFeedsPage } from "@/pages/crm-feeds-page";
import { LoginPage } from "@/pages/login-page";
import { QueuePage } from "@/pages/queue-page";
import { SettingsPage } from "@/pages/settings-page";
import { SuppressionsPage } from "@/pages/suppressions-page";

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<RequireUser />}>
        <Route path="/accounts" element={<AccountsPage />} />
        <Route path="/accounts/:accountId/queue" element={<QueuePage />} />
        <Route path="/accounts/:accountId/queue/:callbackId" element={<QueuePage />} />
        <Route path="/accounts/:accountId/settings" element={<SettingsPage />} />
        {/*
          One agent's own setup — the hook, its channels, its context. Its own URL rather than a
          panel on Configuration, because it is the screen an FDE works in for twenty minutes and
          needs to be able to send someone a link to.
        */}
        <Route path="/accounts/:accountId/agents/:agentId" element={<AgentPage />} />
        <Route path="/accounts/:accountId/suppressions" element={<SuppressionsPage />} />
        {/*
          Under the account, and plural: a CRM feed dials through an account and an account may
          have several. The list redirects to the feed when there is only one of them.
        */}
        <Route path="/accounts/:accountId/crm" element={<CrmFeedsPage />} />
        <Route path="/accounts/:accountId/crm/:customer" element={<CrmFeedPage />} />
        <Route path="/accounts/:accountId" element={<RedirectToQueue />} />
      </Route>

      <Route path="*" element={<Navigate to="/accounts" replace />} />
    </Routes>
  );
}

function RequireUser() {
  const location = useLocation();
  const { data, isPending, isError } = useMe();

  if (isPending) return <div className="h-screen bg-muted" />;

  if (isError || !data?.user) {
    // `from` so a bookmarked queue URL survives the detour through the login form.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <Outlet />;
}

/** `/accounts/:id` on its own is a mis-typed URL, not a page. Send it somewhere useful. */
function RedirectToQueue() {
  const { pathname } = useLocation();
  return <Navigate to={`${pathname.replace(/\/$/, "")}/queue`} replace />;
}
